import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CardContent, Typography, FormControl, FormGroup, Grid, Box } from "@mui/material";

import { AppSwitch } from "../../../../../../../components/Generic/AppSwitch";
import CheckboxComponent from "../../../../../../../components/Form/CheckboxComponent/CheckboxComponent";
import BadgeStatus from "Components/Generic/BadgeStatus";
import RadioButton, { OptionProps } from "Components/Form/RadioGroup";
import { StatusBadgeEnum } from "Util/enums";

import { Group } from "../../../../../../../models/group";
import {
  GroupMfaSettings,
  MfaMethod,
  MfaEnvironment,
} from "../../../../../../../services/group-mfa-settings.service";

import {
  StyledCard,
  GroupHeader,
  SettingsSection,
  Loading,
  Title,
  SubTitle,
  Message,
} from "./MfaGroupCard.styles";

interface MfaGroupCardProps {
  group: Group;
  settings?: GroupMfaSettings;
  onUpdate: (
    groupId: number,
    enabled: boolean,
    method?: MfaMethod,
    environments?: MfaEnvironment[]
  ) => void;
  isLoading?: boolean;
}

const MfaGroupCard: React.FC<MfaGroupCardProps> = ({
  group,
  settings,
  onUpdate,
  isLoading = false,
}) => {
  const { t: translate } = useTranslation();
  const groupId = group.id;

  const [enabled, setEnabled] = useState(settings?.enabled || false);
  const [method, setMethod] = useState<MfaMethod>(settings?.method || MfaMethod.EMAIL);
  const [environments, setEnvironments] = useState<MfaEnvironment[]>(settings?.environments || []);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled);
      setMethod(settings.method || MfaMethod.EMAIL);
      setEnvironments(settings.environments || []);
    }
  }, [settings]);

  if (groupId == null) return null;

  const handleEnabledChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setEnabled(checked);
    onUpdate(groupId, checked, checked ? method : undefined, checked ? environments : []);
  };

  const handleMethodChange = (value: string) => {
    const newMethod = value as MfaMethod;
    setMethod(newMethod);

    if (enabled) {
      onUpdate(groupId, true, newMethod, environments);
    }
  };

  const handleEnvironmentChange = (environment: MfaEnvironment) => {
    const newEnvironments = environments.includes(environment)
      ? environments.filter((env) => env !== environment)
      : [...environments, environment];

    setEnvironments(newEnvironments);

    if (enabled) {
      onUpdate(groupId, true, method, newEnvironments);
    }
  };

  const getEnvironmentLabel = (env: MfaEnvironment): string => {
    switch (env) {
      case MfaEnvironment.WEB:
        return translate("web");
      case MfaEnvironment.OPERATIONS_APP:
        return translate("operationsApp");
      case MfaEnvironment.SALES_APP:
        return translate("salesApp");
      default:
        return env;
    }
  };

  const mfaMethodOptions: OptionProps[] = useMemo(
    () => [
      {
        key: "mfa-method-email",
        value: MfaMethod.EMAIL,
        label: translate("email"),
        disabled: isLoading,
      },
      {
        key: "mfa-method-sms",
        value: MfaMethod.SMS,
        label: translate("sms"),
        disabled: isLoading,
      },
    ],
    [translate, isLoading]
  );

  return (
    <StyledCard>
      <CardContent>
        <GroupHeader>
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Title>{group.name}</Title>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {enabled && <BadgeStatus value={StatusBadgeEnum.MFA_ENABLED} />}
            {isLoading ? (
              <Loading size={42} />
            ) : (
              <AppSwitch checked={enabled} onChange={handleEnabledChange} disabled={isLoading} />
            )}
          </Box>
        </GroupHeader>

        {enabled && (
          <SettingsSection>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <RadioButton
                  label={translate("authenticationMethod")}
                  value={method}
                  onChange={(_: React.ChangeEvent<HTMLInputElement>, value: string) =>
                    handleMethodChange(value)
                  }
                  options={mfaMethodOptions}
                  disabled={isLoading}
                  isDirectionRow={false}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" fullWidth>
                  <SubTitle>{translate("enabledEnvironments")}</SubTitle>

                  <FormGroup>
                    <CheckboxComponent
                      checked={environments.includes(MfaEnvironment.WEB)}
                      onChange={() => handleEnvironmentChange(MfaEnvironment.WEB)}
                      label={getEnvironmentLabel(MfaEnvironment.WEB)}
                      disabled={isLoading}
                    />
                    <CheckboxComponent
                      checked={environments.includes(MfaEnvironment.OPERATIONS_APP)}
                      onChange={() => handleEnvironmentChange(MfaEnvironment.OPERATIONS_APP)}
                      label={getEnvironmentLabel(MfaEnvironment.OPERATIONS_APP)}
                      disabled={isLoading}
                    />
                    <CheckboxComponent
                      checked={environments.includes(MfaEnvironment.SALES_APP)}
                      onChange={() => handleEnvironmentChange(MfaEnvironment.SALES_APP)}
                      label={getEnvironmentLabel(MfaEnvironment.SALES_APP)}
                      disabled={isLoading}
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>

            {method === MfaMethod.SMS && (
              <Box mt={2}>
                <Message>
                  <strong>{translate("note")}:</strong> {translate("smsRequiresPhoneNumber")}
                </Message>
              </Box>
            )}
          </SettingsSection>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default MfaGroupCard;
